import React from 'react';
import { X, ExternalLink, CheckCircle2, ShieldAlert } from 'lucide-react';

interface MethodologyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  toolName: string;
  source: string;
  formulaExplanation: string;
  jurisdictionDate: string;
  assumptions: string[];
}

export const MethodologyDrawer: React.FC<MethodologyDrawerProps> = ({
  isOpen,
  onClose,
  toolName,
  source,
  formulaExplanation,
  jurisdictionDate,
  assumptions,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto shadow-xl space-y-5 p-6">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">
              Transparent Methodology & Trust Layer
            </div>
            <h3 className="text-lg font-bold text-[#1A1A1A] mt-0.5">
              How {toolName} Works
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Source & Date */}
        <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
          <div className="flex items-center justify-between text-xs font-bold text-[#1A1A1A]">
            <span>Official Data Source</span>
            <span className="text-[#6B7280] font-normal">{jurisdictionDate}</span>
          </div>
          <p className="text-xs text-[#6B7280] leading-relaxed">
            {source}
          </p>
        </div>

        {/* Mathematical Model */}
        <div className="space-y-2">
          <h4 className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">
            Mathematical Formulation
          </h4>
          <div className="p-3 bg-[#F3F4F6] rounded-xl border border-[#E5E7EB] text-xs font-mono text-[#1A1A1A] leading-relaxed">
            {formulaExplanation}
          </div>
        </div>

        {/* Assumptions */}
        <div className="space-y-2">
          <h4 className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">
            Baseline Assumptions & Disclaimers
          </h4>
          <ul className="space-y-1.5 text-xs text-[#6B7280]">
            {assumptions.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer Notice */}
        <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-[11px] text-amber-950 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <span>
            YamooPay provides mathematical models for educational and informational purposes. Consult certified tax advisors or financial fiduciaries for legal filing binding decisions.
          </span>
        </div>

        <div className="pt-2 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl transition-all"
          >
            Got it, return to calculator
          </button>
        </div>
      </div>
    </div>
  );
};
